import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import {
  ShieldCheck,
  Crosshair,
  Brain,
  Award,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

const pillars = [
  {
    icon: Award,
    title: "Two decades of contracting depth",
    body: "Built from nearly 20 years of hands-on federal and commercial contracting experience — capture, source selection, proposal evaluation, contract administration, and small business programs.",
  },
  {
    icon: Brain,
    title: "AI applied where judgment matters",
    body: "Claude Sonnet 4.5 is paired with rigorously structured prompts, FAR/DFARS schema, and source-selection methodology — not surface-level summarization.",
  },
  {
    icon: Crosshair,
    title: "Decisions in minutes, not weeks",
    body: "GovBidIQ compresses the capture-management workflow into a single defensible report so contractors can make faster, more informed Bid / No-Bid decisions.",
  },
];

const tenets = [
  "Decision intelligence — never document summarization.",
  "Executive-grade brevity. No fluff. No hedging.",
  "Surface what experienced capture managers see — and what novices miss.",
  "Anchor every assessment in scoring, evidence, and prioritized action.",
  "State assumptions when information is unavailable. Never guess silently.",
];

export default function About() {
  return (
    <div className="grain min-h-screen">
      <Navbar />

      <section className="border-b border-[var(--border-soft)]">
        <div className="max-w-[1100px] mx-auto px-6 py-20">
          <div className="tick-label text-sky-400 mb-4">/ About GovBidIQ</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl text-slate-100 leading-[1.05] mb-6 max-w-3xl">
            Built by contracting practitioners.
            <br />
            <span className="text-slate-500">Powered by AI that respects the work.</span>
          </h1>
          <p className="text-slate-300 text-lg leading-relaxed max-w-3xl">
            GovBidIQ is an AI-powered government contract intelligence platform built on nearly two
            decades of federal and commercial contracting experience. We help federal contractors
            make faster, sharper, more defensible Bid / No-Bid decisions — without the cost or
            cycle time of a traditional capture consultancy.
          </p>
        </div>
      </section>

      <section className="border-b border-[var(--border-soft)]">
        <div className="max-w-[1100px] mx-auto px-6 py-20">
          <div className="grid md:grid-cols-3 gap-px bg-[var(--border-soft)]">
            {pillars.map((p) => (
              <div key={p.title} className="bg-[var(--bg-primary)] p-7">
                <p.icon className="w-7 h-7 text-sky-400 mb-5" strokeWidth={1.4} />
                <h3 className="font-display font-bold text-slate-100 text-base mb-2">{p.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-[var(--border-soft)]">
        <div className="max-w-[1100px] mx-auto px-6 py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="tick-label text-sky-400 mb-3">/ Principles</div>
            <h2 className="font-display font-black text-3xl text-slate-100 leading-tight mb-6">
              What we believe about government contract analysis.
            </h2>
            <p className="text-slate-400 leading-relaxed text-sm">
              These tenets shape every report GovBidIQ produces and every roadmap decision we make.
            </p>
          </div>
          <div className="lg:col-span-7 space-y-px bg-[var(--border-soft)]">
            {tenets.map((t) => (
              <div key={t} className="bg-[var(--bg-primary)] p-5 flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-sky-400 flex-shrink-0 mt-1" />
                <span className="text-slate-200 text-sm leading-relaxed">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-[var(--border-soft)]">
        <div className="max-w-[1100px] mx-auto px-6 py-20 text-center">
          <ShieldCheck className="w-10 h-10 text-sky-400 mx-auto mb-6" strokeWidth={1.4} />
          <h2 className="font-display font-black text-3xl text-slate-100 mb-4 max-w-2xl mx-auto">
            Faster, more informed Bid / No-Bid decisions.
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto mb-8">
            Stop chasing the wrong RFPs. Walk into pursuit reviews with executive-grade evidence.
          </p>
          <Link
            to="/pricing"
            data-testid="about-cta-pricing"
            className="inline-flex items-center gap-3 bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-xs tracking-[0.2em] uppercase font-bold px-7 py-4 transition-colors"
          >
            View Pricing <ArrowRight className="w-4 h-4" strokeWidth={3} />
          </Link>
        </div>
      </section>
    </div>
  );
}
